import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery, gql } from '@apollo/client';
import { toast } from 'react-toastify';
import { Button } from '../button';
import { Modal } from '../modal';
import { URLS } from '../../../constants/urls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard,
  faCheck,
  faTimes,
  faExclamationTriangle,
  faRedo,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import NumericInput from '../NumericInput';

import DLinCameraImg from '../../../assets/images/DLinCamera.jpeg';
import DLSelfieImg from '../../../assets/images/DL selfie.jpeg';

// Step flow type
type DLStep =
  | 'dl-instruction'
  | 'dl-camera'
  | 'dl-preview'
  | 'selfie-instruction'
  | 'selfie-camera'
  | 'selfie-preview'
  | 'review';

// GraphQL queries and mutations
const GET_PRESIGNED_URL_FOR_DL = gql`
  mutation GetPresignedUrlForDLVerification(
    $fileType: String!
    $bookingId: String!
    $photoType: String!
  ) {
    getPresignedUrlForDLVerification(
      fileType: $fileType
      bookingId: $bookingId
      photoType: $photoType
    ) {
      url
      key
    }
  }
`;

const CAN_SUBMIT_DL_VERIFICATION = gql`
  query CanSubmitDLVerification($bookingId: String!) {
    canSubmitDLVerification(bookingId: $bookingId)
  }
`;

const SUBMIT_DL_VERIFICATION = gql`
  mutation SubmitDLVerification($input: SubmitDLVerificationInput!) {
    submitDLVerification(input: $input) {
      id
      dlVerificationStatus
      dlSubmittedAt
    }
  }
`;

const VERIFY_GUEST_DL = gql`
  mutation VerifyGuestDL($bookingId: String!, $licenceExpirationDate: String) {
    verifyGuestDL(bookingId: $bookingId, licenceExpirationDate: $licenceExpirationDate) {
      id
      dlVerificationStatus
      dlVerifiedAt
    }
  }
`;

const REJECT_DL_AND_CANCEL = gql`
  mutation RejectDLAndCancelBooking($input: RejectDLInput!) {
    rejectDLAndCancelBooking(input: $input) {
      id
      dlVerificationStatus
      dlRejectionReason
      status
    }
  }
`;

// Styled components
const Section = styled.div`
  ${tw`bg-white rounded-lg p-4 mb-4`}
`;

const SectionTitle = styled.h3`
  ${tw`text-lg font-semibold mb-4 flex items-center gap-2`}
`;

const StatusBadge = styled.span<{ status: string }>`
  ${tw`px-3 py-1 rounded-full text-sm font-medium`}
  ${({ status }) => {
    switch (status) {
      case 'PENDING':
        return tw`bg-yellow-100 text-yellow-800`;
      case 'SUBMITTED':
        return tw`bg-blue-100 text-blue-800`;
      case 'VERIFIED':
        return tw`bg-green-100 text-green-800`;
      case 'REJECTED':
        return tw`bg-red-100 text-red-800`;
      default:
        return tw`bg-bg-secondary text-text-primary`;
    }
  }}
`;

const ClickableStatusBadge = styled.button<{ status: string }>`
  ${tw`px-3 py-1 rounded-md text-sm font-medium cursor-pointer transition-all border-none ml-2 mb-2`}
  &:hover {
    ${tw`bg-blue-800 text-white`}
  }
  ${({ status }) => {
    switch (status) {
      case 'PENDING':
        return tw`bg-yellow-100 text-yellow-800`;
      case 'SUBMITTED':
        return tw`bg-blue-100 text-blue-800`;
      case 'VERIFIED':
        return tw`bg-green-100 text-green-800`;
      case 'REJECTED':
        return tw`bg-red-100 text-red-800`;
      default:
        return tw`bg-bg-secondary text-text-primary`;
    }
  }}
`;

// Helper function to get display status
const getDisplayStatus = (status: string | null | undefined): string => {
  switch (status) {
    case 'SUBMITTED':
      return 'Press here to review (Awaiting Host Review)';
    case 'VERIFIED':
      return 'VERIFIED';
    case 'REJECTED':
      return 'REJECTED';
    case 'PENDING':
    default:
      return 'PENDING';
  }
};

const PhotoUploadArea = styled.div<{ hasImage?: boolean }>`
  ${tw`border-2 border-dashed border-border-default rounded-lg p-6 text-center cursor-pointer transition-colors overflow-hidden`}
  ${({ hasImage }) =>
    hasImage ? tw`border-green-500 bg-green-50` : tw`hover:border-gray-500 hover:bg-bg-secondary`}
  max-height: 16rem;
`;

const PhotoPreview = styled.img`
  ${tw`w-full rounded-lg mx-auto`}
  max-height: 12rem;
  object-fit: contain;
`;

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4 mb-4`}
`;

const PhotoCard = styled.div`
  ${tw`flex flex-col overflow-hidden`}
`;

const PhotoLabel = styled.label`
  ${tw`text-sm font-medium text-text-secondary mb-2`}
`;

const PhotoInstructions = styled.p`
  ${tw`text-xs text-text-secondary mt-2`}
`;

const ImagePreviewContainer = styled.div`
  ${tw`relative w-full overflow-hidden rounded-lg bg-bg-secondary border border-border-default flex items-center justify-center`}
  aspect-ratio: 4 / 3;
`;

const HostPhotoPreview = styled.img`
  ${tw`w-full h-full cursor-pointer`}
  object-fit: contain;
`;

const DlFullImageOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background-color: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
`;

const DlFullImage = styled.img`
  display: block;
  max-width: min(100%, calc(100vw - 2rem));
  max-height: min(100%, calc(100vh - 2rem));
  width: auto;
  height: auto;
  object-fit: contain;
`;

const DlFullImageClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  ${tw`flex items-center justify-center w-10 h-10 rounded-full border-none cursor-pointer`}
  background-color: rgba(0, 0, 0, 0.6);
  color: #fff;

  &:hover {
    background-color: rgba(0, 0, 0, 0.85);
  }
`;

const VerificationChecklist = styled.ul`
  ${tw`list-disc list-inside text-sm text-text-secondary mb-4 space-y-1`}
`;

const ButtonGroup = styled.div`
  ${tw`flex gap-3 mt-4`}
`;

const RejectModalContent = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const ModalLabel = styled.label`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const ModalTextArea = styled.textarea`
  ${tw`w-full p-3 border border-border-default rounded-lg resize-none`}
  min-height: 100px;
`;

const ModalInput = styled.input`
  ${tw`w-full p-3 border border-border-default rounded-lg`}
`;

const WarningText = styled.p`
  ${tw`text-sm text-red-600 bg-red-50 p-3 rounded-lg`}
`;

const RejectionReasonBox = styled.div`
  ${tw`bg-red-50 border border-red-200 rounded-lg p-3 mb-4`}
`;

// Step flow styled components
const StepOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepPanel = styled.div`
  ${tw`flex flex-col`}
  width: 100%;
  max-width: 480px;
  height: 100vh;
  max-height: 100vh;
  background-color: #fff;
  overflow: hidden;
  @media (min-width: 480px) {
    height: 90vh;
    max-height: 90vh;
    border-radius: 16px;
  }
`;

const StepContainer = styled.div`
  ${tw`flex-1 flex flex-col`}
  position: relative;
`;

const HeroImageContainer = styled.div`
  width: 100%;
  height: 50vh;
  overflow: hidden;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StepContent = styled.div`
  ${tw`flex-1 flex flex-col items-center justify-center px-8`}
`;

const StepTitle = styled.h2`
  ${tw`text-xl font-bold text-text-primary text-center mb-3`}
`;

const StepDescription = styled.p`
  ${tw`text-base text-text-secondary text-center leading-relaxed max-w-sm`}
`;

const StickyFooterBtn = styled.button`
  ${tw`w-full py-4 font-semibold text-lg cursor-pointer border-none`}
  background-color: #eb5a1c;
  color: #fff;
  &:hover {
    background-color: #d4510f;
  }
`;

const CloseStepButton = styled.button`
  ${tw`absolute cursor-pointer bg-transparent border-none p-2`}
  top: 20px;
  left: 20px;
  z-index: 10;
`;

const PreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 99999;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewPanel = styled.div`
  ${tw`flex flex-col items-center justify-center`}
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background-color: #000;
  @media (min-width: 480px) {
    height: 90vh;
    border-radius: 16px;
    overflow: hidden;
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: calc(100vh - 140px);
  object-fit: contain;
`;

const PreviewActions = styled.div`
  ${tw`flex items-center justify-center gap-10 mt-6`}
`;

const ActionCircle = styled.button<{ bgColor?: string }>`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background-color: ${({ bgColor }) => bgColor || 'rgba(255,255,255,0.9)'};
  ${tw`flex items-center justify-center cursor-pointer border-none`}
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
  &:hover {
    transform: scale(1.05);
  }
  transition: transform 0.15s;
`;

const ReviewContainer = styled.div`
  ${tw`flex-1 flex flex-col items-center px-8 pt-16 overflow-y-auto`}
  position: relative;
`;

const ReviewPhotoCard = styled.div`
  ${tw`w-full max-w-lg mb-4 rounded-card overflow-hidden border-2 border-border-default cursor-pointer`}
  &:hover {
    border-color: #eb5a1c;
  }
  transition: border-color 0.15s;
`;

const ReviewPhotoImage = styled.img`
  ${tw`w-full`}
  height: 200px;
  object-fit: cover;
`;

const ReviewPhotoLabel = styled.div`
  ${tw`flex items-center justify-between px-4 py-3 bg-bg-secondary`}
`;

const ReviewPhotoLabelText = styled.span`
  ${tw`text-sm font-semibold text-text-secondary`}
`;

const RetakeHint = styled.span`
  ${tw`text-xs text-text-secondary`}
`;

const OutlinedActionButton = styled.button<{ disabled?: boolean }>`
  ${tw`w-full max-w-lg py-3 rounded-lg font-semibold text-base cursor-pointer mt-2 mb-8`}
  border: 2px solid ${({ disabled }) => (disabled ? 'var(--color-border)' : '#eb5a1c')};
  color: ${({ disabled }) => (disabled ? 'var(--color-text-tertiary)' : '#eb5a1c')};
  background-color: #fff;
  &:hover {
    background-color: ${({ disabled }) => (disabled ? '#fff' : '#fff7f4')};
  }
`;

const VerifyNowButton = styled.button`
  ${tw`py-3 px-8 font-semibold text-sm cursor-pointer border-none rounded-lg mt-4`}
  background-color: #eb5a1c;
  color: #fff;
  &:hover {
    background-color: #d4510f;
  }
`;

interface DLVerificationProps {
  bookingId: string;
  isHost: boolean;
  dlVerificationStatus?: string | null;
  dlPhoto?: string | null;
  dlSelfiePhoto?: string | null;
  /** Spec 026: signed links from the API; the photo keys are only a fallback. */
  dlPhotoUrl?: string | null;
  dlSelfiePhotoUrl?: string | null;
  dlRejectionReason?: string | null;
  dlSubmittedAt?: string | null;
  dlVerifiedAt?: string | null;
  tripStartTime: number;
  totalTripAmount: number;
  onUpdate?: () => void;
  autoOpenVerify?: boolean;
  onAutoOpenHandled?: () => void;
  /** Hides the inline "Verify My DL Now" button — used when parent renders its own CTA */
  hideButton?: boolean;
  /** Registers the startStepFlow function so parent can trigger it from its own button */
  onRegisterVerify?: (fn: () => void) => void;
}

export const DLVerification: React.FC<DLVerificationProps> = ({
  bookingId,
  isHost,
  dlVerificationStatus,
  dlPhoto,
  dlSelfiePhoto,
  dlPhotoUrl,
  dlSelfiePhotoUrl,
  dlRejectionReason,
  dlSubmittedAt,
  dlVerifiedAt,
  tripStartTime,
  totalTripAmount,
  onUpdate,
  autoOpenVerify,
  onAutoOpenHandled,
  hideButton,
  onRegisterVerify,
}) => {
  const [dlPhotoFile, setDlPhotoFile] = useState<File | null>(null);
  // Spec 026 D4: the expiry date the host reads off the licence at pickup (yyyy-MM-dd).
  const [licenceExpiry, setLicenceExpiry] = useState('');
  const guestDlSrc =
    dlPhotoUrl || (dlPhoto ? `${URLS.S3_BUCKET.DL_VERIFICATION}/${dlPhoto}` : null);
  const guestSelfieSrc =
    dlSelfiePhotoUrl ||
    (dlSelfiePhoto ? `${URLS.S3_BUCKET.DL_VERIFICATION}/${dlSelfiePhoto}` : null);
  const [dlPhotoPreview, setDlPhotoPreview] = useState<string | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmDLModal, setShowConfirmDLModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [showFullImage, setShowFullImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState<'dl' | 'selfie' | null>(null);
  const [showStepFlow, setShowStepFlow] = useState(false);
  const [currentStep, setCurrentStep] = useState<DLStep>('dl-instruction');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Auto-open verify modal for host when triggered from parent
  useEffect(() => {
    if (autoOpenVerify && isHost && dlVerificationStatus === 'SUBMITTED') {
      setShowConfirmDLModal(true);
      onAutoOpenHandled?.();
    }
  }, [autoOpenVerify]);

  // Check if within 24 hours of trip start
  const { data: canSubmitData } = useQuery(CAN_SUBMIT_DL_VERIFICATION, {
    variables: { bookingId },
    skip: isHost || dlVerificationStatus === 'VERIFIED',
  });

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_DL);
  const [submitDLVerification, { loading: submitLoading }] = useMutation(SUBMIT_DL_VERIFICATION);
  const [verifyGuestDL, { loading: verifyLoading }] = useMutation(VERIFY_GUEST_DL);
  const [rejectDLAndCancel, { loading: rejectLoading }] = useMutation(REJECT_DL_AND_CANCEL);

  const canSubmit = canSubmitData?.canSubmitDLVerification || false;

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const startCamera = useCallback(async (type: 'dl' | 'selfie') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: type === 'selfie' ? 'user' : 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      setIsCapturing(type);
      setCurrentStep(type === 'dl' ? 'dl-camera' : 'selfie-camera');

      setTimeout(() => {
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.play().catch(() => {
            toast.error('Failed to start camera preview');
            stopCamera();
          });
        }
      }, 100);
    } catch (error: any) {
      toast.error(error.message || 'Failed to access camera. Please allow camera permissions.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCapturing(null);
  }, []);

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !isCapturing) return;

    const video = videoRef.current;
    if (video.readyState !== video.HAVE_ENOUGH_DATA || video.videoWidth === 0) {
      toast.error('Camera is not ready. Please wait a moment and try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    if (ctx) {
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        blob => {
          if (blob) {
            const file = new File([blob], `${isCapturing}-${Date.now()}.jpg`, {
              type: 'image/jpeg',
            });
            const reader = new FileReader();
            reader.onload = e => {
              if (isCapturing === 'dl') {
                setDlPhotoFile(file);
                setDlPhotoPreview(e.target?.result as string);
                setCurrentStep('dl-preview');
              } else {
                setSelfieFile(file);
                setSelfiePreview(e.target?.result as string);
                setCurrentStep('selfie-preview');
              }
            };
            reader.readAsDataURL(file);
          }
        },
        'image/jpeg',
        0.9,
      );
    }
    stopCamera();
  }, [isCapturing, stopCamera]);

  const handleRetake = useCallback(
    (type: 'dl' | 'selfie') => {
      if (type === 'dl') {
        setDlPhotoFile(null);
        setDlPhotoPreview(null);
      } else {
        setSelfieFile(null);
        setSelfiePreview(null);
      }
      startCamera(type);
    },
    [startCamera],
  );

  const retakeFromReview = useCallback((type: 'dl' | 'selfie') => {
    if (type === 'dl') {
      setDlPhotoFile(null);
      setDlPhotoPreview(null);
      setCurrentStep('dl-instruction');
    } else {
      setSelfieFile(null);
      setSelfiePreview(null);
      setCurrentStep('selfie-instruction');
    }
  }, []);

  const startStepFlow = useCallback(() => {
    setCurrentStep('dl-instruction');
    setShowStepFlow(true);
  }, []);

  useEffect(() => {
    if (onRegisterVerify) {
      onRegisterVerify(startStepFlow);
    }
  }, [onRegisterVerify, startStepFlow]);

  const closeStepFlow = useCallback(() => {
    stopCamera();
    setShowStepFlow(false);
  }, [stopCamera]);

  const uploadToS3 = async (file: File, photoType: 'dl' | 'selfie'): Promise<string> => {
    const { data } = await getPresignedUrl({
      variables: {
        fileType: file.type,
        bookingId,
        photoType,
      },
    });

    const { url, key } = data.getPresignedUrlForDLVerification;

    // Upload to S3
    await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    return key;
  };

  const handleSubmit = async () => {
    if (!dlPhotoFile || !selfieFile) {
      toast.error("Please upload both your driver's license and selfie");
      return;
    }

    setIsUploading(true);
    try {
      // Upload both photos
      const [dlKey, selfieKey] = await Promise.all([
        uploadToS3(dlPhotoFile, 'dl'),
        uploadToS3(selfieFile, 'selfie'),
      ]);

      // Submit verification
      await submitDLVerification({
        variables: {
          input: {
            bookingId,
            dlPhotoKey: dlKey,
            selfiePhotoKey: selfieKey,
          },
        },
      });

      toast.success('DL verification submitted successfully!');
      setDlPhotoFile(null);
      setDlPhotoPreview(null);
      setSelfieFile(null);
      setSelfiePreview(null);
      setShowStepFlow(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit DL verification');
    } finally {
      setIsUploading(false);
    }
  };

  const handleVerify = async (): Promise<boolean> => {
    try {
      await verifyGuestDL({
        variables: { bookingId, licenceExpirationDate: licenceExpiry || null },
      });
      toast.success('DL verification confirmed!');
      onUpdate?.();
      return true;
    } catch (error: any) {
      // Spec 026: a licence that ends before the trip is refused with the reason.
      toast.error(error.message || 'Failed to verify DL');
      return false;
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    const refundValue = parseFloat(refundAmount) || 0;
    if (refundValue < 0 || refundValue > totalTripAmount) {
      toast.error(`Refund amount must be between $0 and $${totalTripAmount.toFixed(2)}`);
      return;
    }

    try {
      await rejectDLAndCancel({
        variables: {
          input: {
            bookingId,
            reason: rejectReason.trim(),
            refundAmount: refundValue,
          },
        },
      });
      toast.success('DL rejected and booking cancelled');
      setShowRejectModal(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to reject DL');
    }
  };

  const renderStepFlow = () => {
    switch (currentStep) {
      case 'dl-instruction':
        return (
          <StepOverlay>
            <StepPanel>
              <StepContainer>
                <CloseStepButton onClick={closeStepFlow}>
                  <FontAwesomeIcon icon={faTimes} size="lg" color="#fff" />
                </CloseStepButton>
                <HeroImageContainer>
                  <HeroImage src={DLinCameraImg} alt="DL Photo Instructions" />
                </HeroImageContainer>
                <StepContent>
                  <StepTitle>Driver's License Photo</StepTitle>
                  <StepDescription>
                    Please take a clear photo of your Valid Driver's License. Make sure all 4
                    corners are visible.
                  </StepDescription>
                </StepContent>
                <StickyFooterBtn onClick={() => startCamera('dl')}>Ready</StickyFooterBtn>
              </StepContainer>
            </StepPanel>
          </StepOverlay>
        );

      case 'dl-camera':
      case 'selfie-camera':
        return (
          <PreviewOverlay>
            <PreviewPanel>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <p
                  style={{ color: '#fff', fontSize: '18px', fontWeight: 500, marginBottom: '12px' }}
                >
                  {currentStep === 'dl-camera'
                    ? "Take Driver's License Photo"
                    : "Take Selfie with Driver's License"}
                </p>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 140px)' }}
                />
                <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                  <button
                    onClick={() => {
                      stopCamera();
                      setCurrentStep(
                        currentStep === 'dl-camera' ? 'dl-instruction' : 'selfie-instruction',
                      );
                    }}
                    style={{
                      width: '120px',
                      padding: '10px 0',
                      borderRadius: '8px',
                      border: '2px solid #fff',
                      backgroundColor: 'transparent',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={takePhoto}
                    style={{
                      width: '120px',
                      padding: '10px 0',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#fff',
                      color: '#000',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Capture
                  </button>
                </div>
              </div>
            </PreviewPanel>
          </PreviewOverlay>
        );

      case 'dl-preview':
        return (
          <PreviewOverlay>
            <PreviewPanel>
              <PreviewImage src={dlPhotoPreview!} alt="DL Photo" />
              <PreviewActions>
                <ActionCircle onClick={() => handleRetake('dl')}>
                  <FontAwesomeIcon icon={faRedo} size="lg" color="var(--color-text-secondary)" />
                </ActionCircle>
                <ActionCircle
                  bgColor="#eb5a1c"
                  onClick={() => setCurrentStep('selfie-instruction')}
                >
                  <FontAwesomeIcon icon={faArrowRight} size="lg" color="#fff" />
                </ActionCircle>
              </PreviewActions>
            </PreviewPanel>
          </PreviewOverlay>
        );

      case 'selfie-instruction':
        return (
          <StepOverlay>
            <StepPanel>
              <StepContainer>
                <CloseStepButton onClick={closeStepFlow}>
                  <FontAwesomeIcon icon={faTimes} size="lg" color="#fff" />
                </CloseStepButton>
                <HeroImageContainer>
                  <HeroImage src={DLSelfieImg} alt="Selfie Instructions" />
                </HeroImageContainer>
                <StepContent>
                  <StepTitle>Selfie with Driver's License</StepTitle>
                  <StepDescription>
                    Please take a selfie while holding your DL in hand. Make sure both your face and
                    license are clearly visible.
                  </StepDescription>
                </StepContent>
                <StickyFooterBtn onClick={() => startCamera('selfie')}>Ready</StickyFooterBtn>
              </StepContainer>
            </StepPanel>
          </StepOverlay>
        );

      case 'selfie-preview':
        return (
          <PreviewOverlay>
            <PreviewPanel>
              <PreviewImage src={selfiePreview!} alt="Selfie Photo" />
              <PreviewActions>
                <ActionCircle onClick={() => handleRetake('selfie')}>
                  <FontAwesomeIcon icon={faRedo} size="lg" color="var(--color-text-secondary)" />
                </ActionCircle>
                <ActionCircle bgColor="#eb5a1c" onClick={() => setCurrentStep('review')}>
                  <FontAwesomeIcon icon={faArrowRight} size="lg" color="#fff" />
                </ActionCircle>
              </PreviewActions>
            </PreviewPanel>
          </PreviewOverlay>
        );

      case 'review':
        return (
          <StepOverlay>
            <StepPanel>
              <ReviewContainer>
                <CloseStepButton onClick={closeStepFlow}>
                  <FontAwesomeIcon icon={faTimes} size="lg" color="var(--color-text-secondary)" />
                </CloseStepButton>
                <StepTitle style={{ marginBottom: '24px' }}>Review Your Photos</StepTitle>

                <ReviewPhotoCard onClick={() => retakeFromReview('dl')}>
                  <ReviewPhotoImage src={dlPhotoPreview!} alt="DL" />
                  <ReviewPhotoLabel>
                    <ReviewPhotoLabelText>Driver's License</ReviewPhotoLabelText>
                    <RetakeHint>Click to retake</RetakeHint>
                  </ReviewPhotoLabel>
                </ReviewPhotoCard>

                <ReviewPhotoCard onClick={() => retakeFromReview('selfie')}>
                  <ReviewPhotoImage src={selfiePreview!} alt="Selfie" />
                  <ReviewPhotoLabel>
                    <ReviewPhotoLabelText>Selfie with DL</ReviewPhotoLabelText>
                    <RetakeHint>Click to retake</RetakeHint>
                  </ReviewPhotoLabel>
                </ReviewPhotoCard>

                <OutlinedActionButton
                  onClick={handleSubmit}
                  disabled={!dlPhotoFile || !selfieFile || isUploading || submitLoading}
                >
                  {isUploading || submitLoading ? 'Submitting...' : 'Submit DL Verification'}
                </OutlinedActionButton>
              </ReviewContainer>
            </StepPanel>
          </StepOverlay>
        );
    }
  };

  // Guest view
  if (!isHost) {
    // Don't show if already verified or if not within 24h window
    if (dlVerificationStatus === 'VERIFIED') {
      return (
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faCheck} tw="text-green-600" />
            Driver's License Verified
          </SectionTitle>
          <StatusBadge status="VERIFIED">Verified</StatusBadge>
          <p tw="text-sm text-text-secondary mt-2">
            Your driver's license has been verified by the host.
          </p>
        </Section>
      );
    }

    if (!canSubmit && dlVerificationStatus !== 'SUBMITTED' && dlVerificationStatus !== 'REJECTED') {
      return null; // Don't show section if not within 24h window
    }

    return (
      <>
        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faIdCard} tw="text-text-secondary" />
            Verify My DL Now
          </SectionTitle>

          <div tw="flex items-center gap-2 mb-4">
            <span>Status:</span>
            <StatusBadge status={dlVerificationStatus || 'PENDING'}>
              {getDisplayStatus(dlVerificationStatus)}
            </StatusBadge>
          </div>

          {dlVerificationStatus === 'SUBMITTED' && (
            <p tw="text-sm text-text-secondary mb-4">
              Your DL verification is waiting for host review.
            </p>
          )}

          {dlVerificationStatus === 'REJECTED' && dlRejectionReason && (
            <RejectionReasonBox>
              <p tw="text-sm text-red-800 font-medium">Rejection Reason:</p>
              <p tw="text-sm text-red-700">{dlRejectionReason}</p>
            </RejectionReasonBox>
          )}

          {!hideButton &&
            (dlVerificationStatus === 'PENDING' || dlVerificationStatus === 'REJECTED') &&
            canSubmit && (
              <VerifyNowButton onClick={startStepFlow}>Verify My DL Now</VerifyNowButton>
            )}
        </Section>

        {/* Step-by-step DL verification flow — fullscreen portal */}
        {showStepFlow && ReactDOM.createPortal(renderStepFlow(), document.body)}
      </>
    );
  }

  // Host view - show button/link to open modal instead of showing content directly
  if (dlVerificationStatus !== 'SUBMITTED' && dlVerificationStatus !== 'VERIFIED') {
    return null; // Don't show section if no verification to review
  }

  // Close modal and reset reject modal state when closing confirm modal
  const handleCloseConfirmModal = () => {
    setShowConfirmDLModal(false);
    setShowRejectModal(false);
    setRejectReason('');
    setRefundAmount('');
  };

  // Close reject modal when verifying successfully
  const handleVerifyAndClose = async () => {
    // Stays open on a refusal, so the host can read why and reject the licence.
    if (await handleVerify()) {
      setShowConfirmDLModal(false);
    }
  };

  return (
    <>
      <Section>
        <SectionTitle>
          <FontAwesomeIcon icon={faIdCard} tw="text-text-secondary" />
          Confirm Guest DL
        </SectionTitle>

        <div tw="flex items-center gap-2 mb-4">
          <span>Status:</span>
          {dlVerificationStatus === 'SUBMITTED' ? (
            <ClickableStatusBadge
              status={dlVerificationStatus || 'PENDING'}
              onClick={() => setShowConfirmDLModal(true)}
            >
              {getDisplayStatus(dlVerificationStatus)}
            </ClickableStatusBadge>
          ) : (
            <StatusBadge status={dlVerificationStatus || 'PENDING'}>
              {getDisplayStatus(dlVerificationStatus)}
            </StatusBadge>
          )}
        </div>

        {dlVerificationStatus === 'VERIFIED' ? (
          <p tw="text-sm text-green-600">You have verified this guest's driver's license.</p>
        ) : (
          <p tw="text-sm text-text-secondary">
            Guest's DL verification is waiting for your review.
          </p>
        )}
      </Section>

      {/* Confirm Guest DL Modal */}
      <Modal
        isOpen={showConfirmDLModal}
        onClose={handleCloseConfirmModal}
        title="Confirm Guest DL"
        modalWidth="min(800px, calc(100vw - 40px))"
      >
        <div>
          <div tw="flex items-center gap-2 mb-4">
            <span>Status:</span>
            <StatusBadge status={dlVerificationStatus || 'PENDING'}>
              {getDisplayStatus(dlVerificationStatus)}
            </StatusBadge>
          </div>

          <p tw="text-sm text-text-secondary mb-4">
            Guest's DL verification is waiting for your review.
          </p>

          <PhotoGrid>
            {guestDlSrc && (
              <PhotoCard>
                <PhotoLabel>Driver's License</PhotoLabel>
                <ImagePreviewContainer>
                  <HostPhotoPreview
                    src={guestDlSrc}
                    alt="Guest DL"
                    onClick={() => setShowFullImage(guestDlSrc)}
                  />
                </ImagePreviewContainer>
              </PhotoCard>
            )}
            {guestSelfieSrc && (
              <PhotoCard>
                <PhotoLabel>Selfie with DL</PhotoLabel>
                <ImagePreviewContainer>
                  <HostPhotoPreview
                    src={guestSelfieSrc}
                    alt="Guest Selfie with DL"
                    onClick={() => setShowFullImage(guestSelfieSrc)}
                  />
                </ImagePreviewContainer>
              </PhotoCard>
            )}
          </PhotoGrid>

          <VerificationChecklist>
            <li>Verify name matches the guest's name on the booking</li>
            <li>Verify date of birth is visible and valid</li>
            <li>Verify face in selfie matches the license photo</li>
            <li>Verify license is not expired</li>
          </VerificationChecklist>

          <div tw="mb-4">
            <ModalLabel htmlFor="dl-licence-expiry">Expiry date on the license *</ModalLabel>
            <ModalInput
              id="dl-licence-expiry"
              type="date"
              value={licenceExpiry}
              onChange={e => setLicenceExpiry(e.target.value)}
            />
            <p tw="text-xs text-text-secondary mt-1">
              Enter the date printed on the license you are holding. It must not end before the trip
              does.
            </p>
          </div>

          <ButtonGroup>
            <Button
              text={verifyLoading ? 'Confirming...' : 'Confirm DL'}
              onClick={handleVerifyAndClose}
              buttonWidth="150px"
              theme="filled"
              isLoading={verifyLoading}
              isDisabled={verifyLoading || !licenceExpiry}
            />
            <Button
              text="Reject DL"
              onClick={() => setShowRejectModal(true)}
              buttonWidth="150px"
              theme="outlined"
            />
          </ButtonGroup>
        </div>
      </Modal>

      {showFullImage &&
        ReactDOM.createPortal(
          <DlFullImageOverlay
            role="dialog"
            aria-modal="true"
            aria-label="DL verification photo"
            onClick={() => setShowFullImage(null)}
          >
            <DlFullImageClose
              type="button"
              aria-label="Close"
              onClick={() => setShowFullImage(null)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </DlFullImageClose>
            <DlFullImage
              src={showFullImage}
              alt="DL verification"
              onClick={e => e.stopPropagation()}
            />
          </DlFullImageOverlay>,
          document.body,
        )}

      {/* Reject DL Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject DL Verification"
      >
        <RejectModalContent>
          <div>
            <ModalLabel>Rejection Reason *</ModalLabel>
            <ModalTextArea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Why are you rejecting this DL verification?"
            />
          </div>

          <div>
            <ModalLabel>Refund Amount *</ModalLabel>
            <NumericInput
              allowDecimal={true}
              value={refundAmount ? parseFloat(refundAmount) : null}
              onValueChange={(value: number | null) => setRefundAmount(value?.toString() ?? '')}
              placeholder="0.00"
              minValue={0}
              maxValue={totalTripAmount}
              width="100%"
            />
            <p tw="text-xs text-text-secondary mt-1">
              Total trip amount: ${totalTripAmount.toFixed(2)}
            </p>
          </div>

          <WarningText>
            <FontAwesomeIcon icon={faExclamationTriangle} tw="mr-2" />
            Warning: This will cancel the trip and immediately refund the guest.
          </WarningText>

          <ButtonGroup>
            <Button
              text="Cancel"
              onClick={() => setShowRejectModal(false)}
              buttonWidth="150px"
              theme="outlined"
            />
            <Button
              text={rejectLoading ? 'Processing...' : 'Confirm Rejection'}
              onClick={async () => {
                await handleReject();
                setShowConfirmDLModal(false);
              }}
              buttonWidth="150px"
              theme="filled"
              isLoading={rejectLoading}
              isDisabled={rejectLoading || !rejectReason.trim()}
            />
          </ButtonGroup>
        </RejectModalContent>
      </Modal>
    </>
  );
};

export default DLVerification;
